import type { AgentRow, ChannelRow, DmRow, PageRow } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { Avatar, Badge, Icon, IconButton, ListRow, Menu, SectionHeader, type MenuItem } from "../primitives/index.ts";
import type { ReactNode } from "react";
import { PageIcon } from "./Pages.tsx";
import { StatusEmoji } from "./StatusEmoji.tsx";
import { useT } from "../i18n/I18nProvider.tsx";

/** `menu`: more actions after open-in-tab/split (star, mute, move to folder, leave…). */
type RowProps<R> = { row: R; selected: boolean; onPress: () => void; onOpenInNewTab?: () => void; onOpenInSplit?: () => void; menu?: MenuItem[] };

function contextMenu(t: ReturnType<typeof useT>, p: { onOpenInNewTab?: () => void; onOpenInSplit?: () => void; menu?: MenuItem[] }): MenuItem[] {
  const items: MenuItem[] = [];
  if (p.onOpenInNewTab !== undefined) items.push({ id: "new-tab", label: t("shell.openInNewTab"), icon: "tabs", onSelect: p.onOpenInNewTab });
  if (p.onOpenInSplit !== undefined) items.push({ id: "split", label: t("shell.newTabInSplit"), icon: "splitH", onSelect: p.onOpenInSplit });
  if (p.menu !== undefined) items.push(...p.menu);
  return items;
}

/** External marker + mention count (Slack: unread without a mention is just bold); undefined when there is nothing to show. */
function trailing(p: { external?: boolean; status?: ReactNode; unread: boolean; mentions: number }): ReactNode {
  const badge = p.mentions > 0 ? <Badge count={p.mentions} /> : null;
  if (p.external !== true && p.status == null && badge === null) return undefined;
  return (
    <>
      {p.external === true ? <Icon name="external" size="icon.sm" color="external.fg" /> : null}
      {p.status}
      {badge}
    </>
  );
}

/** Home / Activity / Agents entries at the top of the sidebar. */
export function SidebarNavRow(p: { icon: IconName; label: string; badge?: number; selected: boolean; onPress: () => void; testID: string }) {
  return (
    <ListRow
      title={p.label}
      leading={<Icon name={p.icon} size="icon.sm" color={p.selected ? "text.primary" : "text.secondary"} />}
      trailing={p.badge === undefined || p.badge === 0 ? undefined : <Badge count={p.badge} />}
      current={p.selected}
      onPress={p.onPress}
      testID={p.testID}
    />
  );
}

/**
 * `emoji` (a folder's label) shows instead of `icon`; `addMenu` turns "+" into a menu; `menu` adds an options ("…")
 * menu (folders: edit, reorder, delete). The buttons show on hover / keyboard focus (SectionHeader compact).
 */
export function SidebarSectionHeader(p: {
  title: string;
  icon: IconName;
  emoji?: string;
  collapsed: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  addLabel?: string;
  addMenu?: MenuItem[];
  menu?: MenuItem[];
  menuLabel?: string;
  testID: string;
}) {
  const options =
    p.menu === undefined ? null : (
      <Menu items={p.menu} align="end" testID={`${p.testID}-menu`}>
        <IconButton icon="more" size="sm" accessibilityLabel={p.menuLabel ?? p.title} onPress={() => {}} testID={`${p.testID}-options`} />
      </Menu>
    );
  const add =
    p.addMenu !== undefined ? (
      <Menu items={p.addMenu} align="end" testID={`${p.testID}-add-menu`}>
        <IconButton icon="plus" size="sm" accessibilityLabel={p.addLabel ?? p.title} onPress={() => {}} testID={`${p.testID}-add`} />
      </Menu>
    ) : p.onAdd === undefined ? null : (
      <IconButton icon="plus" size="sm" accessibilityLabel={p.addLabel ?? p.title} onPress={p.onAdd} testID={`${p.testID}-add`} />
    );
  return (
    <SectionHeader
      title={p.title}
      icon={p.icon}
      emoji={p.emoji}
      collapsed={p.collapsed}
      onToggle={p.onToggle}
      trailing={options === null && add === null ? undefined : <>{options}{add}</>}
      testID={p.testID}
    />
  );
}

export function SidebarChannelRow(p: RowProps<ChannelRow>) {
  const t = useT();
  const r = p.row;
  const strong = r.unread || r.mentions > 0;
  return (
    <ListRow
      title={r.name}
      leading={<Icon name={r.isPrivate ? "lock" : "hash"} size="icon.sm" color={strong || p.selected ? "text.primary" : "text.tertiary"} />}
      trailing={trailing({ external: r.external, unread: r.unread, mentions: r.mentions })}
      current={p.selected}
      emphasized={strong}
      muted={r.muted}
      onPress={p.onPress}
      contextMenu={contextMenu(t, p)}
      testID={`sidebar-channel-${r.channelId}`}
    />
  );
}

export function SidebarDmRow(p: RowProps<DmRow>) {
  const t = useT();
  const r = p.row;
  return (
    <ListRow
      title={r.name}
      leading={
        r.isGroup ? (
          // Slack: a group DM shows how many people are in it.
          <Avatar name={r.name} emoji={String(r.userIds.length)} size="avatar.xs" square />
        ) : (
          <Avatar name={r.avatarName} src={r.avatarUrl} presence={r.presence} dnd={r.dnd} dndLabel={t("dnd.paused")} size="avatar.xs" square={r.isAgent} />
        )
      }
      titleAccessory={r.status === undefined ? undefined : <StatusEmoji person={r} testID={`sidebar-dm-${r.channelId}-status`} />}
      trailing={trailing({ external: r.external, unread: r.unread, mentions: r.mentions })}
      current={p.selected}
      emphasized={r.unread || r.mentions > 0}
      onPress={p.onPress}
      contextMenu={contextMenu(t, p)}
      testID={`sidebar-dm-${r.channelId}`}
    />
  );
}

export function SidebarAgentRow(p: RowProps<AgentRow>) {
  const t = useT();
  const r = p.row;
  const busy = r.state === "working" || r.state === "waiting" || r.state === "error";
  return (
    <ListRow
      title={r.name}
      leading={<Avatar name={r.name} src={r.avatarUrl} presence={r.presence} size="avatar.xs" square />}
      trailing={trailing({
        status: busy ? <Icon name={r.state === "error" ? "alert" : r.state === "waiting" ? "hourglass" : "sparkles"} size="icon.sm" color={r.state === "error" ? "status.danger" : "agent.fg"} /> : null,
        unread: r.unread,
        mentions: r.mentions,
      })}
      current={p.selected}
      emphasized={r.unread || r.mentions > 0}
      onPress={p.onPress}
      contextMenu={contextMenu(t, p)}
      testID={`sidebar-agent-${r.userId}`}
    />
  );
}

/** A docs item (page, database, board) in one of my folders. */
export function SidebarFolderPageRow(p: { row: PageRow; selected: boolean; onPress: () => void; menu?: MenuItem[]; onOpenInNewTab?: () => void; onOpenInSplit?: () => void }) {
  const t = useT();
  const r = p.row;
  return (
    <ListRow
      title={r.name === "" ? t("page.untitled") : r.name}
      leading={<PageIcon icon={r.icon} kind={r.pageKind} />}
      current={p.selected}
      onPress={p.onPress}
      contextMenu={contextMenu(t, p)}
      testID={`sidebar-page-${r.pageId}`}
    />
  );
}
